/**
 * The "Stock" / "Central Stock" switch of the stock screens: asks for a
 * confirmation, then writes the `stocksActive` / `centralStocksActive`
 * configuration entry. The switch only moves once the API has answered.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  appConfigsQueryKey,
  appConfigsQueryOptions,
  configFlag,
  updateAppConfig,
  type AppConfigEntry,
} from '@/shared/config/app-configs';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { SlideToggle } from '@/shared/ui/SlideToggle';

/** The cached configuration entries with `type` set to `value` (added when missing); untouched when not cached as a list. */
const withConfigValue = (
  current: unknown,
  type: string,
  value: boolean,
): unknown => {
  if (!Array.isArray(current)) return current;
  const entries = current as AppConfigEntry[];
  if (!entries.some((entry) => entry.type === type))
    return [...entries, { type, value }];
  return entries.map((entry) =>
    entry.type === type ? { ...entry, value } : entry,
  );
};

export interface StockToggleProps {
  configType: 'stocksActive' | 'centralStocksActive';
  label: string;
}

export function StockToggle({ configType, label }: Readonly<StockToggleProps>) {
  const queryClient = useQueryClient();
  const configs = useQuery(appConfigsQueryOptions());
  const active = configFlag(configs.data, configType);
  const [confirming, setConfirming] = useState(false);

  const update = useMutation({
    mutationFn: (value: boolean) => updateAppConfig(configType, value),
    onSuccess: (_result, value) => {
      queryClient.setQueryData(appConfigsQueryKey, (current) =>
        withConfigValue(current, configType, value),
      );
    },
    onError: () => toast.error('Unexpected error, please try again later'),
  });

  return (
    <>
      <SlideToggle
        id={`toggle-${configType}`}
        className="mr-12"
        checked={active}
        disabled={update.isPending}
        onChange={() => setConfirming(true)}
        testId={
          configType === 'stocksActive'
            ? 'stock-toggle'
            : 'central-stock-toggle'
        }
      >
        {label}
      </SlideToggle>
      {confirming && (
        <ConfirmDialog
          title="Are you sure?"
          type="question"
          text={`Do you want to ${active ? 'deactivate' : 'activate'} the stock integration?`}
          onConfirm={() => {
            setConfirming(false);
            update.mutate(!active);
          }}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  );
}
